/**
 * ProtectedRoute — gate for all dashboard routes.
 *
 * Reads the current session from `api.pfh.trading/api/auth/session`.
 * If unauthenticated, bounces the browser to `pfh.trading/login` with
 * a `?next=` param so the login flow returns the user to the page
 * they were trying to visit.
 *
 * No login UI lives in the dashboard itself — single source of truth
 * is the landing app's `/login`.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { Loader2 } from "@/components/ui/icons";
import { useSession } from "@/lib/auth-client";

const SESSION_TIMEOUT_MS = 6000;

function bounceToLogin() {
  const here = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  const loginBase =
    window.location.hostname === "localhost"
      ? "http://localhost:5173/login"
      : "https://pfh.trading/login";
  window.location.replace(`${loginBase}?next=${encodeURIComponent(here)}`);
}

export function ProtectedRoute(): ReactNode {
  const { data, isPending, error } = useSession();
  const [timedOut, setTimedOut] = useState(false);

  // Safety net: if useSession is stuck in pending (e.g. Safari ITP blocking
  // the cookie, network hang), fail open to login after a few seconds
  // instead of leaving the user staring at a blank screen.
  useEffect(() => {
    if (!isPending) return;
    const t = window.setTimeout(() => setTimedOut(true), SESSION_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [isPending]);

  // Fetch error or timeout → bounce to login. The login flow re-mints the
  // session cookie and brings the user back here.
  if (error || timedOut) {
    bounceToLogin();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <p className="text-sm text-muted-foreground">Redirecting to sign in…</p>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-accent" />
          <p className="text-sm text-muted-foreground">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (!data?.session) {
    bounceToLogin();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <p className="text-sm text-muted-foreground">Redirecting to sign in…</p>
      </div>
    );
  }

  return <Outlet />;
}
